/**
 * Pure inventory logic (CC-6: NEVER use Lingui macros here).
 *
 * Quantities and costs are stored in INTEGERS (minor units) to
 * avoid float rounding. Scale factor is 1000 (thousandths), same
 * as the minor units pattern in cashsync.
 */

/** Scale factor: 1 UI unit = 1000 stored minor units. */
export const QTY_SCALE = 1000;

/** Converts a UI quantity (e.g. 12.5) to integer minor units. */
export function toMinorQty(display: number): number {
  return Math.round(display * QTY_SCALE);
}

/** Converts stored minor units to UI quantity. */
export function fromMinorQty(minor: number): number {
  return minor / QTY_SCALE;
}

/**
 * Formats minor units as readable text. Without Lingui macros: the caller
 * passes the already resolved separator if localization is needed.
 */
export function formatQty(minor: number, fractionDigits = 2): string {
  return fromMinorQty(minor)
    .toFixed(fractionDigits)
    .replace(/\.?0+$/, "");
}

/**
 * Parses user input text to minor units. Accepts comma or dot
 * as decimal separator. Returns null if not a valid number.
 */
export function parseQtyInput(raw: string): number | null {
  const normalized = raw.trim().replace(",", ".");
  if (normalized === "") {
    return null;
  }
  const value = Number(normalized);
  if (!Number.isFinite(value)) {
    return null;
  }
  return toMinorQty(value);
}

/**
 * Calculates new on-hand following an inventory adjustment. The adjustment is the
 * COUNTED quantity (target); returns delta (move) and new on-hand.
 */
export function computeAdjustment(
  currentOnHand: number,
  countedOnHand: number,
): { delta: number; newOnHand: number } {
  return { delta: countedOnHand - currentOnHand, newOnHand: countedOnHand };
}

/** Applies a move (delta) to current on-hand, deriving the new balance. */
export function applyMove(currentOnHand: number, delta: number): number {
  return currentOnHand + delta;
}
